<script setup lang="ts">
  import { toTypedSchema } from '@vee-validate/zod';
  import { storeToRefs } from 'pinia';
  import { useForm } from 'vee-validate';
  import { computed, ref, watch } from 'vue';

  import { Alert, AlertDescription, AlertTitle } from '@ui/components/alert';
  import { Avatar, AvatarFallback } from '@ui/components/avatar';
  import { Button } from '@ui/components/button';
  import { Calendar } from '@ui/components/calendar';
  import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/components/form';
  import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/popover';
  import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
  } from '@ui/components/select';
  import { Skeleton } from '@ui/components/skeleton';
  import { toast } from '@ui/components/sonner';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  import { stuut } from '@/api';
  import { useEmailSenders } from '@/store';
  import { getApiErrorMessage } from '@/utils/apiErrors';

  import {
    formatDateLabel,
    getInitialOutOfOfficeFormValues,
    getOutOfOfficeSubmitLabel,
    getSharedInboxCandidate,
    outOfOfficeFormSchema,
    useOutOfOfficeResponderForm,
  } from './useOutOfOfficeResponderForm';
  import type {
    OutOfOfficeFormDefaults,
    OutOfOfficeFormValues,
    SharedInboxEmailSender,
  } from './useOutOfOfficeResponderForm';

  const props = withDefaults(
    defineProps<{
      backupUserId?: string;
      currentUserId?: string;
      firstDay?: Date | string | null;
      hasExistingOutOfOffice?: boolean;
      lastDay?: Date | string | null;
      sharedInboxEmail?: string;
      showCancel?: boolean;
      targetUserId: string;
    }>(),
    {
      backupUserId: undefined,
      currentUserId: undefined,
      firstDay: undefined,
      hasExistingOutOfOffice: false,
      lastDay: undefined,
      sharedInboxEmail: undefined,
      showCancel: true,
    },
  );

  const emit = defineEmits<{
    cancelled: [];
    saved: [];
  }>();

  const { activeBackupUsers, isLoading, isSaving, loadError, loadedPreferences, save, users } =
    useOutOfOfficeResponderForm({
      currentUserId: () => props.currentUserId,
      hasExistingOutOfOffice: () => props.hasExistingOutOfOffice,
      targetUserId: () => props.targetUserId,
    });

  const emailSenderStore = useEmailSenders();
  const { visibleEmailSenders } = storeToRefs(emailSenderStore);

  const validationSchema = toTypedSchema(outOfOfficeFormSchema);

  const initialFormDefaults = computed<string | OutOfOfficeFormDefaults | undefined>(() => {
    if (props.firstDay || props.lastDay) {
      return {
        backupUserId: props.backupUserId,
        firstDay: props.firstDay,
        lastDay: props.lastDay,
      };
    }

    return props.backupUserId;
  });

  const form = useForm<OutOfOfficeFormValues>({
    initialValues: getInitialOutOfOfficeFormValues(null, initialFormDefaults.value),
    validationSchema,
    validateOnMount: true,
  });

  const [backupUserId] = form.defineField('backupUserId');
  const [firstDay] = form.defineField('firstDay');
  const [lastDay] = form.defineField('lastDay');

  const selectedBackupUser = computed(() =>
    users.value.find((user) => user.id === backupUserId.value),
  );

  const backupInitials = computed(() => {
    const user = selectedBackupUser.value;
    if (!user) return '?';
    return `${user.givenName?.[0] ?? ''}${user.familyName?.[0] ?? ''}` || '?';
  });

  // Re-seed initial values once preferences load or props arrive — but skip if
  // the user has started editing, so prop reactivity doesn't clobber edits.
  watch([loadedPreferences, initialFormDefaults], ([preferences, defaults]) => {
    if (form.meta.value.dirty) return;
    const initial = getInitialOutOfOfficeFormValues(preferences, defaults);
    form.resetForm({ values: initial });
  });

  const onSubmit = form.handleSubmit(async (values) => {
    const didSave = await save(values);
    if (didSave) emit('saved');
  });

  const handleCancel = () => {
    const wasDirty = form.meta.value.dirty;
    form.resetForm({
      values: getInitialOutOfOfficeFormValues(loadedPreferences.value, initialFormDefaults.value),
    });
    if (wasDirty) toast.message('Changes discarded');
    emit('cancelled');
  };

  watch(
    () => props.sharedInboxEmail,
    async (email) => {
      if (!email) return;
      try {
        await emailSenderStore.fetchEmailSenders();
      } catch {
        toast.error('Could not load linked email settings');
      }
    },
    { immediate: true },
  );

  const sharedInboxEmailSender = computed(() =>
    getSharedInboxCandidate({
      currentUserId: props.currentUserId,
      email: props.sharedInboxEmail,
      emailSenders: visibleEmailSenders.value,
    }),
  );

  const shouldShowSharedInboxPrompt = computed(() => !!sharedInboxEmailSender.value);

  const markSharedInboxSaved = (emailSender: SharedInboxEmailSender) => {
    emailSenderStore.patchEmailSender(emailSender.id, {
      is_shared: true,
    });
    toast.success(`${emailSender.email} shared with the organization.`);
  };

  const isSharedInboxSaving = ref(false);

  const makeSharedInbox = async () => {
    const emailSender = sharedInboxEmailSender.value;
    if (!emailSender || isSharedInboxSaving.value) return;

    isSharedInboxSaving.value = true;

    try {
      await stuut.emailSenders.update(emailSender.id, { data: { is_shared: true } });
      markSharedInboxSaved(emailSender);
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Could not share email with the organization'));
    } finally {
      isSharedInboxSaving.value = false;
    }
  };

  const isFormDisabled = computed(
    () => isLoading.value || isSaving.value || isSharedInboxSaving.value || !!loadError.value,
  );

  const submitLabel = computed(() => getOutOfOfficeSubmitLabel(props.hasExistingOutOfOffice));
</script>

<template>
  <form class="flex flex-col gap-6" @submit.prevent="onSubmit">
    <Alert v-if="loadError" variant="destructive">
      <i class="far fa-triangle-exclamation" />
      <AlertDescription>{{ loadError }}</AlertDescription>
    </Alert>

    <Alert v-if="shouldShowSharedInboxPrompt">
      <i class="far fa-inbox" />
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex flex-col gap-1">
          <AlertTitle>Share Your Email Thread</AlertTitle>
          <AlertDescription>
            To send your email communications to another member on your team when you are out of
            office, your email needs to be shared with the organization.
          </AlertDescription>
        </div>
        <Button
          type="button"
          size="sm"
          class="shrink-0 gap-2"
          :disabled="isFormDisabled"
          @click="makeSharedInbox"
        >
          <i
            class="text-2xs"
            :class="isSharedInboxSaving ? 'far fa-spinner-third animate-spin' : 'far fa-inbox'"
          />
          {{ isSharedInboxSaving ? 'Updating…' : 'Share with Organization' }}
        </Button>
      </div>
    </Alert>

    <div v-if="isLoading" class="grid grid-cols-1 gap-4 xl:grid-cols-3">
      <div v-for="i in 3" :key="i" class="flex flex-col gap-3">
        <Skeleton class="h-4 w-24" />
        <Skeleton class="h-9 w-full" />
      </div>
    </div>

    <div v-else class="grid grid-cols-1 gap-4 xl:grid-cols-3">
      <FormField name="firstDay">
        <FormItem class="min-w-0 gap-3">
          <FormLabel>First Day</FormLabel>
          <Popover>
            <PopoverTrigger as-child>
              <FormControl>
                <Button
                  variant="outline"
                  class="h-9 w-full justify-between px-3 font-normal shadow-xs"
                  :disabled="isFormDisabled"
                >
                  <span class="truncate">{{ formatDateLabel(firstDay) ?? 'Select date' }}</span>
                  <i class="far fa-calendar shrink-0 text-muted-foreground" />
                </Button>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent class="w-auto p-0">
              <Calendar v-model="firstDay" />
            </PopoverContent>
          </Popover>
          <FormMessage class="text-xs" />
        </FormItem>
      </FormField>

      <FormField name="lastDay">
        <FormItem class="min-w-0 gap-3">
          <FormLabel>Last Day (optional)</FormLabel>
          <Popover>
            <PopoverTrigger as-child>
              <FormControl>
                <Button
                  variant="outline"
                  class="h-9 w-full justify-between px-3 font-normal shadow-xs"
                  :disabled="isFormDisabled"
                >
                  <span class="truncate" :class="!lastDay ? 'text-muted-foreground' : ''">
                    {{ formatDateLabel(lastDay) ?? 'Select date' }}
                  </span>
                  <i class="far fa-calendar shrink-0 text-muted-foreground" />
                </Button>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent class="w-auto p-0">
              <Calendar v-model="lastDay" />
            </PopoverContent>
          </Popover>
          <FormMessage class="text-xs" />
        </FormItem>
      </FormField>

      <FormField name="backupUserId">
        <FormItem class="min-w-0 gap-3 xl:max-w-md">
          <div class="flex items-center gap-1.5">
            <FormLabel>Backup</FormLabel>
            <Tooltip>
              <TooltipTrigger as-child>
                <i class="fa-regular fa-circle-info text-sm" />
              </TooltipTrigger>
              <TooltipContent class="max-w-xs">
                When you are unavailable, chosen backup can receive or field replies on your behalf.
              </TooltipContent>
            </Tooltip>
          </div>

          <FormControl>
            <Select v-model="backupUserId" :disabled="isFormDisabled">
              <SelectTrigger
                class="h-9 w-full shadow-xs [&_[data-slot=select-value]]:flex [&_[data-slot=select-value]]:min-w-0 [&_[data-slot=select-value]]:items-center [&_[data-slot=select-value]]:gap-2"
              >
                <span class="flex min-w-0 flex-1 items-center gap-2">
                  <Avatar class="size-6 shrink-0">
                    <AvatarFallback class="text-2xs">{{ backupInitials }}</AvatarFallback>
                  </Avatar>
                  <SelectValue placeholder="Select backup" />
                </span>
              </SelectTrigger>
              <SelectContent side="bottom">
                <SelectItem v-for="user in activeBackupUsers" :key="user.id" :value="user.id">
                  {{ user.name }}
                </SelectItem>
              </SelectContent>
            </Select>
          </FormControl>
          <FormMessage class="text-xs" />
        </FormItem>
      </FormField>
    </div>

    <div v-if="isLoading" class="flex flex-wrap gap-2">
      <Skeleton class="h-9 w-24" />
      <Skeleton v-if="showCancel" class="h-9 w-20" />
    </div>

    <div v-else class="flex flex-wrap gap-2">
      <Button type="submit" variant="outline" :disabled="isFormDisabled">
        {{ submitLabel }}
      </Button>
      <Button
        v-if="showCancel"
        type="button"
        variant="ghost"
        :disabled="isLoading || isSaving"
        @click="handleCancel"
      >
        Cancel
      </Button>
    </div>
  </form>
</template>
